import { ErrorHandler, Injectable } from '@angular/core';
import { CoreButtonStyle } from '@core-modules/components/core-button/core-button.component';
import { CoreHttpClient } from '@core-modules/providers/core-http-client/core-http-client';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { EnvironmentProvider } from '@core-modules/providers/environment-variables/environment';
import { NavController } from '@ionic/angular';
import { AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { MessagePageOptions } from 'messages/pages/message/message.page.data';
import { MessageIcon } from 'messages/pages/message/message.page.icons';
import { MessagePageController } from '../../../messages/providers/message-page-controller';
import { ApplicationError } from '../../business-objects/application-error';
import { DeeplinkApplicationError } from '../../business-objects/application-error.deeplink';
import { HttpApplicationError } from '../../business-objects/application-error.http';
import { RuntimeApplicationError } from '../../business-objects/application-error.runtime';
import { HTTPErrorCode } from '../../business-objects/http-error-codes';
import { CoreServerError } from '../../models/core-server-error';

/*
 * Generated class for the ErrorHandlerProvider provider.
 */
@Injectable()
export class CoreErrorHandler implements ErrorHandler
{

    //#region Constructors

    /**
     * Class constructor
     */
    constructor
        (
            private navCtrl: NavController,
            private http: CoreHttpClient,
            private env: EnvironmentProvider,
            private device: DeviceProvider,
            private messagePageController: MessagePageController
        )
    {
    }

    //#endregion


    //#region Properties

    /**
     * Error endpoint URL
     */
    private readonly errorEndpointUrl: string = `${this.env.apiEndpoint}/Errors/LogMobileApplicationError`;


    //#endregion


    //#region Methods


    //#region Navigation To Error Pages

    /**
     * Go to the network error page
     */
    private goToNoConnectionErrorPage()
    {
        let buttons = [];

        // if the device is an application show the user a button
        // to go to settings and turn on the connection
        if (this.device.isApplication())
        {
            buttons.push(...[
                {
                    text: 'Open Settings',
                    style: CoreButtonStyle.Outline,
                    action: () =>
                    {
                        // get the device's respective settings object and open it
                        let settings: AndroidSettings | IOSSettings = (this.device.isAndroid() ? AndroidSettings.Wifi : IOSSettings.WiFi)
                        this.device.openSettings(settings);
                    }
                },
                {
                    text: 'Continue',
                    isEnabled: this.device.onNetworkStatusChange.asObservable(),
                    action: () =>
                    {
                        this.continueButtonHandler();
                    }
                }
            ]);
        } else
        {
            buttons.push({
                text: 'Continue',
                action: () =>
                {
                    this.continueButtonHandler();
                },
            });
        }

        this.messagePageController
            .goToMessagePage
            ({
                icon: MessageIcon.WIFI,
                buttons: buttons,
                messageExclaimation: "Oops!",
                messageTagline: "You have no connection",
                messageDescription: "Check you have a network connection"
            });
    }

    /**
     * Go to the request error page
     */
    private goToRequestErrorPage()
    {
        this.messagePageController
            .goToMessagePage
            ({
                icon: MessageIcon.GENERIC,
                buttons: [
                    {
                        text: 'Continue',
                        style: CoreButtonStyle.Solid,
                        action: () =>
                        {
                            this.continueButtonHandler();
                        }
                    }],
                messageExclaimation: `Ouch!`,
                messageTagline: `One sec...`,
                messageDescription: `Could not communicate with the server!`,
            });
    }

    /**
     * Go to the out of session page
     */
    private goToOutOfSessionPage()
    {
        this.messagePageController
            .goToMessagePage
            ({
                icon: MessageIcon.GENERIC,
                buttons: [
                    {
                        text: 'Continue',
                        style: CoreButtonStyle.Solid,
                        action: () =>
                        {
                            this.continueButtonHandler();
                        }
                    }],
                messageExclaimation: `Oops!`,
                messageTagline: `Your session has expired!`,
                messageDescription: `You've been inactive and your session has expired. Log in again to continue!`,
            });
    }

    /**
     * Go to the server error page
     */
    private goToServerErrorPage(coreError: CoreServerError)
    {
        // specify the message page options for a Core server error
        let options: MessagePageOptions = {
            icon: MessageIcon.GENERIC,
            buttons: [
                {
                    text: 'Email Support',
                    style: CoreButtonStyle.Outline,
                    action: () =>
                    {
                        this
                            .device
                            .sendEmail
                            (
                                coreError.CustomerSupportEmailAddress,
                                `Core Application Error ${coreError.ErrorCode}`
                            );
                    }
                },
                {
                    text: 'Continue',
                    style: CoreButtonStyle.Solid,
                    action: () =>
                    {
                        // clear the cookies
                        this.http.clearCookies();
                        this.navCtrl.navigateRoot('/landing');
                    }
                }],
            messageExclaimation: `Oops!`,
            messageTagline: `Something went wrong!`,
            messageDescription: coreError.TextMessage
        };

        this.messagePageController
            .goToMessagePage(options);
    }

    /**
     * Go to the under maintenance page
     */
    private goToUnderMaintenancePage()
    {
        this.messagePageController
            .goToMessagePage
            ({
                icon: MessageIcon.MAINTENANCE,
                buttons: [
                    {
                        text: 'Retry',
                        style: CoreButtonStyle.Solid,
                        action: () =>
                        {
                            this.navCtrl.navigateRoot('/landing');
                        }
                    }],
                messageExclaimation: "Under Maintenance!",
                messageTagline: "We'll be back soon!",
                messageDescription: `Core is currently under maintenance, we will be back shortly!`
            });
    }

    /**
     * Go to the Url blocked page
     */
    private goToUrlBlockedPage(error: HttpApplicationError)
    {
        this.messagePageController
            .goToMessagePage
            ({
                icon: MessageIcon.SHIELD,
                messageExclaimation: 'Ouch!',
                messageTagline: 'We could not reach this page!',
                messageDescription: error.message,
                buttons: [
                    {
                        text: 'Continue',
                        style: CoreButtonStyle.Solid,
                        action: () =>
                        {
                            this.navCtrl.navigateRoot('/landing');
                        }
                    }]
            });
    }

    //#endregion


    //#region Error handling

    /**
     * Generic error handler
     * @param error 
     */
    public async handleError(error: any)
    {
        // Handle the error according to its type
        if (error instanceof HttpApplicationError)
        {
            // handle any unhandled http errors
            this.handleHttpError(<HttpApplicationError>(error));
        }
        else if (error instanceof DeeplinkApplicationError)
        {
            // handle any unhandled deeplink errors
            this.logAndShowApplicationError(<DeeplinkApplicationError>(error));
        } else
        {
            if (this.env.logServerErrors)
            {
                this.goToRequestErrorPage();
            }
            else
            {
                //alert(error);
                console.log(error);
            }
        }
    }

    //#endregion

    /**
     * This is the method used to handle go back home button
     * events created in the error page
     */
    private async continueButtonHandler()
    {
        this.navCtrl.navigateRoot('/landing');
    }

    /**
     * Attempt to log an application error, and if successful show the error code page
     * Otherwise show a request error page
     * @param error 
     */
    private async logAndShowApplicationError(error: ApplicationError)
    {
        try
        {
            // attempt to post towards the server and retrieve the
            // code which is associated with the logged request error
            let result = await this.http.post<CoreServerError>(this.errorEndpointUrl, error.toObject());

            // call was successful, display as regular
            // server error page
            this.goToServerErrorPage(result)
        } catch (error)
        {
            // did not manage to log the request error,
            // forward to request error page
            this.goToRequestErrorPage();
        }
    }

    /**
     * Handle runtime error
     * @param error 
     */
    private handleRuntimeError(error: any)
    {
        let applicationVersion = this.env.appVersion;
        let msg = `${error.message}` + (error.rejection ? `. Rejected Promise:${error.rejection}` : '');
        let runtimeError = new RuntimeApplicationError(msg, this.device.getDeviceType(), applicationVersion, this.device.getDeviceSoftwareVersion(), this.device.getDeviceUserAgent(), error.stack);
        this.logAndShowApplicationError(runtimeError.toObject());
    }

    /**
     * Handle an HTTP error response
     * @param errorResponse 
     */
    public async handleHttpError(error: HttpApplicationError)
    {
        if (!this.device.onNetworkStatusChange.value)
        {
            // the user has no internet, redirect to the no connection page
            this.goToNoConnectionErrorPage();
        }
        else if (error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_OUT_OF_SESSION || error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_FORBIDDEN)
        {
            // if the user is out of session redirect him to the out of session page
            this.goToOutOfSessionPage();
        }
        else if (error.HttpStatusCode >= HTTPErrorCode.HTTP_ERROR_UNDER_MAINTENANCE_1 && error.HttpStatusCode <= HTTPErrorCode.HTTP_ERROR_UNDER_MAINTENANCE_3)
        {
            // if the side is currently under maintenance show the under maintenance page
            this.goToUnderMaintenancePage();
        }
        else if (error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_1 || error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_HOST_NOT_FOUND_2)
        {
            // could not resolve host, navigate to request error page
            await this.logAndShowApplicationError(error);
            this.goToUrlBlockedPage(error);
        }
        else if (error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_NOT_FOUND)
        {
            // could not find the requested location
            // navigate to request error page
            await this.logAndShowApplicationError(error);
        } else if (error.HttpStatusCode === HTTPErrorCode.HTTP_ERROR_SERVER_ERROR)
        {
            // navigate to the server error page if the error response received is
            // a JS object from core
            this.goToServerErrorPage(error.CoreServerError);
        }
        else
        {
            // could not find the requested location
            // navigate to request error page
            this.logAndShowApplicationError(error);
        }
    }

    //#endregion

}
